import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { isValidCalendarDate } from '../../../shared/dates';
import { Link } from 'react-router-dom';
import { DateField, formatDayMonth } from '../../components/primitives';
import { CallSheetsIcon } from '../../components/icons';
import { callsheetHref } from '../../lib/toolEmbed';
import { Sheet } from '../../components/Sheet';
import type { CalendarHold } from '../../lib/weekLayout';

export type HoldSelection = { mode: 'add'; day: string } | { mode: 'details' | 'remove'; day: string; hold: CalendarHold };

/** The same form/details content sits in an anchored popover on desktop and
    the existing keyboard-aware sheet on a phone. */
export function HoldPanel({ selection, mobile, onClose, onSaved, onRemove, onConvert, onConfirmRemove }: {
  selection: HoldSelection;
  mobile: boolean;
  onClose: () => void;
  onSaved: () => void;
  onRemove: (hold: CalendarHold) => Promise<void>;
  onConvert: (hold: CalendarHold) => void;
  onConfirmRemove: () => void;
}) {
  const [title, setTitle] = useState('');
  const [from, setFrom] = useState<string | null>(selection.day);
  const [to, setTo] = useState<string | null>(null);
  const [client, setClient] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (mobile) return;
    const opener = document.activeElement as HTMLElement | null;
    (panel.current?.querySelector<HTMLElement>('input, textarea') ?? panel.current?.querySelector<HTMLElement>('button'))?.focus();
    const dismiss = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) { e.stopPropagation(); closeRef.current(); }
    };
    const outside = (e: PointerEvent) => {
      if (!busyRef.current && panel.current && !panel.current.contains(e.target as Node)
          && !(e.target as HTMLElement).closest('.wc-add, .wc-span.hold, .wc-entry.hold, .datepick')) closeRef.current();
    };
    document.addEventListener('keydown', dismiss);
    document.addEventListener('pointerdown', outside);
    return () => {
      document.removeEventListener('keydown', dismiss);
      document.removeEventListener('pointerdown', outside);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [mobile]);

  const save = async () => {
    if (busy) return;
    if (!title.trim() || title.trim().length > 120) { setError('Title must be 1 to 120 characters.'); return; }
    if (!from || !isValidCalendarDate(from) || (to && (!isValidCalendarDate(to) || to < from))) {
      setError('Choose valid dates, with To on or after From.'); return;
    }
    setBusy(true); setError(null);
    try {
      await api('/api/admin/holds', { method: 'POST', json: {
        title: title.trim(), start_date: from, end_date: to, client_text: client.trim() || null, note: note.trim() || null,
      } });
      onSaved(); onClose();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save the hold.'); setBusy(false); }
  };
  const remove = async () => {
    if (selection.mode === 'add' || busy) return;
    setBusy(true); setError(null);
    try { await onRemove(selection.hold); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Could not remove the hold.'); setBusy(false); }
  };
  const adding = selection.mode === 'add';
  const removing = selection.mode === 'remove';
  const heading = adding ? 'Add hold' : removing ? 'Remove hold' : 'Hold details';
  let content: ReactNode;
  let footer: ReactNode;
  if (selection.mode === 'add') {
    content = <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="hold-fields">
      <label className="f-field"><span className="f-label">Title <span className="req">*</span></span>
        <input className="f-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} autoFocus required /></label>
      <div className="hold-two">
        <div className="f-field"><span className="f-label">From</span><DateField keyboardNavigation label="From" value={from} onChange={setFrom} /></div>
        <div className="f-field"><span className="f-label">To <span className="opt">optional</span></span><DateField keyboardNavigation label="To" value={to} onChange={setTo} placeholder="Same day" /></div>
      </div>
      <label className="f-field"><span className="f-label">Client or contact <span className="opt">optional</span></span>
        <input className="f-input" value={client} onChange={(e) => setClient(e.target.value)} placeholder="Free text, e.g. Big Fish Productions" /></label>
      <label className="f-field"><span className="f-label">Note <span className="opt">optional</span></span>
        <textarea className="f-textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why it is on hold, when you will hear back" /></label>
      <button type="submit" hidden />
    </form>;
    footer = <><span className="hint">No project is created</span><button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !title.trim() || !from} onClick={() => void save()}>{busy ? 'Saving…' : 'Save hold'}</button></>;
  } else {
    const hold = selection.hold;
    content = <><div className="hold-pop-head"><span className="wc-hold-tag">HOLD</span><span className="hold-pop-title">{hold.name}</span></div>
      <dl className="hold-meta"><dt>Dates</dt><dd className="mono">{formatDayMonth(hold.start)}{hold.end !== hold.start ? ` – ${formatDayMonth(hold.end)}` : ''}</dd>
        {hold.client_text && <><dt>Client</dt><dd>{hold.client_text}</dd></>}
        {hold.note && <><dt>Note</dt><dd>{hold.note}</dd></>}
        {hold.callsheet_url && <><dt>Source</dt><dd><Link className="cs-chip" to={callsheetHref(hold.callsheet_url)}>
          <CallSheetsIcon />Call sheet{hold.callsheet_version ? ` · v${hold.callsheet_version}` : ''} ↗</Link></dd></>}</dl></>;
    footer = removing
      ? <><span className="hold-remove-question">Remove hold?</span><button className="btn danger" disabled={busy} onClick={() => void remove()}>{busy ? 'Removing…' : 'Remove'}</button><button className="btn" disabled={busy} onClick={onClose}>Keep</button></>
      // A linked hold already has its project, so it opens rather than converts.
      : <>{hold.project_id
          ? <Link className="hold-link" to={`/projects/${hold.project_id}`}>Open project →</Link>
          : <button className="hold-link" onClick={() => onConvert(hold)}>Convert to project →</button>}
        <button className="hold-remove-link" onClick={onConfirmRemove}>Remove</button></>;
  }
  const hint = !adding && !removing && selection.hold.callsheet_url ? <div className="hold-hint">Kept in step with the call sheet</div> : null;
  const body = <>{content}{error && <p role="alert" className="error-text">{error}</p>}</>;
  if (mobile) return <Sheet open className="hold-sheet" variant={adding ? 'full' : 'half'} title={heading} context="Overview · calendar" onClose={onClose}
    canDismiss={() => !busy} leftAction={<button disabled={busy} onClick={onClose}>Cancel</button>}
    footer={<><div className="hold-pop-foot">{footer}</div>{hint}</>}>{body}</Sheet>;
  const dow = new Date(`${selection.day}T00:00:00Z`).getUTCDay();
  return <div ref={panel} className={`hold-pop ${dow === 0 || dow >= 4 ? 'right' : 'left'}`} role="dialog" aria-label={heading}>
    {adding && <div className="hold-pop-head"><span className="hold-pop-title">Add hold</span><span className="modal-context">{formatDayMonth(selection.day)}</span></div>}
    <button className="hold-pop-close" aria-label="Close hold" disabled={busy} onClick={onClose}>×</button>
    {body}<div className="hold-pop-foot">{footer}</div>{hint}
  </div>;
}
